import { useBrowserLocation } from "@folio-2026/browser-location"
import Experience from "@folio-2026/experience"
import { LoadResult } from "@folio-2026/resources"
import { circleMask, EVENTS } from "@folio-2026/shared"
import AnimatedModel from "@folio-2026/shared/animatedmodel"
import assert from "assert"
import { SkeletonUtils } from "three/examples/jsm/Addons.js"
import {
  add,
  cameraPosition,
  color,
  div,
  dot,
  Fn,
  max,
  mul,
  normalize,
  normalWorld,
  positionWorld,
  pow,
  refract,
  texture,
  uniform,
  vec2,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { Object3D } from "three/webgpu"
import { FolderApi } from "tweakpane"

import OuterParticles from "./OuterParticles"

export default class Man {
  experience: Experience
  debugFolder?: FolderApi

  scene: THREE.Object3D
  resources: {
    [key: string]: LoadResult
  }
  animatedModel!: AnimatedModel
  // glowTimeline!: gsap.core.Timeline
  model!: Object3D<THREE.Object3DEventMap>
  perlin!: THREE.Texture
  fbm!: THREE.Texture
  outerParticles!: OuterParticles

  workScene!: THREE.Texture
  envMap!: THREE.Texture

  alreadySSJCoreFinished: boolean = false

  pointLight?: THREE.PointLight

  settings = {
    light: {
      fresnelMultiplier: uniform(0.22),
    },
    circleMask: {
      coordsNDC: uniform(vec2(0, 0.43)),
      radiusNDC: uniform(0.5),
      color: uniform(color("#1a35aa")),
    },
  }

  constructor({
    scene,
    debugFolder,
    resources,
    pointLight,
  }: {
    scene: THREE.Object3D
    debugFolder?: FolderApi
    resources: {
      [key: string]: LoadResult
    }
    pointLight?: THREE.PointLight
  }) {
    this.experience = Experience.getInstance()

    this.scene = scene
    this.resources = resources
    this.debugFolder = debugFolder
    this.pointLight = pointLight

    this.setup()

    const pathname = useBrowserLocation.getState().pathname
    if (pathname === "/") {
      this.animatedModel.startAction("ssj")
    } else {
      this.animatedModel.startAction("ssj-core")
    }

    this.eventListeners()

    this.setDebug()
  }

  setup() {
    const manResource = this.resources["man"]
    assert(manResource, "Man resource not found")
    assert(manResource.scene, "Man resource scene not found")

    this.model = SkeletonUtils.clone(manResource.scene as THREE.Group)
    assert(this.model, "Model not found")

    const animations = manResource.animations || []

    this.animatedModel = new AnimatedModel({
      source: this.model,
      animations,
      container: this.scene as THREE.Scene,
      debugFolder: this.debugFolder!,
    })

    this.setActions()
    // this.setGlowTimeline()

    this.workScene = this.resources["works-scene"] as THREE.Texture
    this.envMap = this.workScene

    this.perlin = this.experience.globalResources.perlin as THREE.Texture
    this.perlin.wrapS = THREE.RepeatWrapping
    this.perlin.wrapT = THREE.RepeatWrapping

    this.fbm = this.experience.globalResources.fbm as THREE.Texture
    this.fbm.wrapS = THREE.RepeatWrapping
    this.fbm.wrapT = THREE.RepeatWrapping

    this.setMaterial()

    // Get the SkinnedMesh directly to ensure same reference
    let skinnedMesh: THREE.SkinnedMesh | null = null
    this.model.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh && !skinnedMesh) {
        skinnedMesh = child
      }
    })
    assert(skinnedMesh, "SkinnedMesh not found in model")

    this.outerParticles = new OuterParticles({
      scene: this.scene,
      man: skinnedMesh,
      debugFolder: this.debugFolder,
      fbm: this.fbm,
      perlin: this.perlin,
    })
  }

  eventListeners() {
    this.experience.on(EVENTS.Works.BeforeEnter, () => {
      this.alreadySSJCoreFinished = false
    })

    this.experience.on(
      EVENTS.Works.BeforeEnter,
      this.beforeEnterAnimation.bind(this),
    )

    this.experience.on(
      EVENTS.Works.Entered,
      this.enterAnimationFinished.bind(this),
    )
  }

  /**
   * Sets the ssj-core action to loop once and clamp when finished
   * This is done once at the beginning so I don't have to do it every time the animation is played
   */
  setActions() {
    const action = this.animatedModel.findAction("ssj-core")
    if (action) {
      action.loop = THREE.LoopOnce
      action.clampWhenFinished = true
    }
  }

  // setGlowTimeline() {
  //   this.glowTimeline = gsap.timeline({ paused: true })

  //   const flashTimeline = gsap.timeline({ repeat: -1 })

  //   for (let i = 0; i < flashPattern.length - 1; i++) {
  //     const [startTime] = flashPattern[i]
  //     const [nextTime, nextIntensity] = flashPattern[i + 1]
  //     flashTimeline.to(
  //       this.settings.glow.glowInternalRadius,
  //       {
  //         value: gsap.utils.clamp(3, 18, 20 - nextIntensity),
  //         duration: nextTime - startTime,
  //         ease: "power2.inOut",
  //       },
  //       startTime,
  //     )
  //   }

  //   this.glowTimeline.add(flashTimeline)
  // }

  setMaterial() {
    if (!this.model) {
      return
    }

    const material = new THREE.MeshBasicNodeMaterial()
    material.side = THREE.FrontSide

    const viewDir = normalize(positionWorld.sub(cameraPosition))
    const inverseFresnel = max(0, dot(viewDir.mul(-1), normalWorld))
    const fresnel = inverseFresnel.oneMinus()

    const colorShader = Fn(() => {
      const baseColor = vec3(1)
      const light = vec3(0, 0.003, 0.005)

      const refFresnel = pow(fresnel, 1)
      const refractDirR = normalize(refract(viewDir, normalWorld, div(1, 1.07)))
      const refractDirG = normalize(
        refract(viewDir, normalWorld, div(1, add(1.07, mul(0.2, refFresnel)))),
      )
      const refractDirB = normalize(
        refract(viewDir, normalWorld, div(1, add(1.07, mul(0.6, refFresnel)))),
      )
      const refractColorR = texture(this.envMap, refractDirR).r.mul(0.5)
      const refractColorG = texture(this.envMap, refractDirG).g.mul(0.5)
      const refractColorB = texture(this.envMap, refractDirB).b
      const refractColor = vec3(refractColorR, refractColorG, refractColorB)

      light.addAssign(
        refractColor.mul(fresnel).mul(this.settings.light.fresnelMultiplier),
      )

      const frColor = baseColor.mul(light)

      const currentCircleMask = circleMask({
        coordsNDC: this.settings.circleMask.coordsNDC,
        radiusNDC: this.settings.circleMask.radiusNDC,
      }).mul(1)
      const circleColor = this.settings.circleMask.color.mul(currentCircleMask)

      const finalColor = frColor.add(circleColor)

      return finalColor
    })

    material.colorNode = colorShader()

    this.model.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh) {
        child.material = material
      }
    })
  }

  beforeEnterAnimation() {
    const activeAction = this.animatedModel.getActiveAction()
    const activeName = activeAction?.getClip().name.toLowerCase()
    if (activeName === "ssj") {
      return
    }

    this.animatedModel.fadeToAction("ssj", 0.2)
    // this.glowTimeline.seek(0).pause()
  }

  /**
   * The enter animation is finished. Note that the enter animation for Works section is
   * a little bit more than just the first viewport. User needs to scroll down a bit more.
   */
  enterAnimationFinished() {
    if (this.alreadySSJCoreFinished) {
      return
    }

    const activeAction = this.animatedModel.getActiveAction()
    const currentAnimationName = activeAction?.getClip().name.toLowerCase()

    if (currentAnimationName !== "ssj-core") {
      this.animatedModel.fadeToAction("ssj-core", 0.3)

      this.alreadySSJCoreFinished = true

      // this.glowTimeline.play()
    }

    // Listen for when ssj-core animation finishes (whether it's already playing or just started)
    if (this.animatedModel.mixer) {
      this.animatedModel.mixer.addEventListener(
        "finished",
        this.ssjCoreFinishedListener.bind(this),
      )
    }
  }

  ssjCoreFinishedListener(event: THREE.AnimationMixerEventMap["finished"]) {
    const clip = event.action.getClip()
    if (clip.name.toLowerCase() === "ssj-core") {
      // Remove the listener
      if (this.animatedModel.mixer) {
        this.animatedModel.mixer.removeEventListener(
          "finished",
          this.ssjCoreFinishedListener.bind(this),
        )
      }
      // Start the loop animation immediately
      this.animatedModel.fadeToAction("ssj-loop", 0.3)
    }
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "👤 Man",
      expanded: false,
    })

    const uniforms = {
      fresnelMultiplier: this.settings.light.fresnelMultiplier.value,
    }

    folder
      .addBinding(uniforms, "fresnelMultiplier", {
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.light.fresnelMultiplier.value = ev.value
      })

    // Circle mask controls
    const circleMaskSettings = {
      coordsNDC: {
        x: this.settings.circleMask.coordsNDC.value.x,
        y: this.settings.circleMask.coordsNDC.value.y,
      },
      radiusNDC: this.settings.circleMask.radiusNDC.value,
      color: `#${new THREE.Color(this.settings.circleMask.color.value).getHexString()}`,
    }

    const circleMaskFolder = folder.addFolder({
      title: "Circle Mask",
      expanded: false,
    })

    circleMaskFolder
      .addBinding(circleMaskSettings.coordsNDC, "x", {
        label: "Coords X",
        min: -1,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        const currentValue = this.settings.circleMask.coordsNDC.value
        this.settings.circleMask.coordsNDC.value = new THREE.Vector2(
          ev.value,
          currentValue.y,
        )
      })

    circleMaskFolder
      .addBinding(circleMaskSettings.coordsNDC, "y", {
        label: "Coords Y",
        min: -1,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        const currentValue = this.settings.circleMask.coordsNDC.value
        this.settings.circleMask.coordsNDC.value = new THREE.Vector2(
          currentValue.x,
          ev.value,
        )
      })

    circleMaskFolder
      .addBinding(circleMaskSettings, "radiusNDC", {
        label: "Radius",
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.circleMask.radiusNDC.value = ev.value
      })

    circleMaskFolder
      .addBinding(circleMaskSettings, "color", {
        view: "color",
      })
      .on("change", (ev) => {
        this.settings.circleMask.color.value = new THREE.Color(ev.value)
      })
  }

  update(params: { delta: number; elapsed: number }) {
    if (this.pointLight) {
      this.settings.circleMask.color.value.copy(this.pointLight.color)
    }
    this.outerParticles?.update()
    this.animatedModel?.update(params)
  }
}
